<template>
  <MCol
    v-if="condition"
    v-show="isElementVisible"
    :cols="cols"
    :width-mode="widthMode"
    :columnIndex="columnIndex"
    :style="[computedStyle, simpleLabelVisibilityStyle]"
    :class="computedClasses"
    class="element-container"
    :is-mediawall="shouldGrowVertically"
    :padding="mColPadding"
    :overflow="mColOverflow"
  >
    <MRow
      :align="elementRowAlign"
      no-gutters
      class="element-container-row"
      :class="{ 'element-container-row--stacked-actions': hasStackedElementActions }"
    >
      <!-- Действия сверху (только position=top; left — WEST, не этот слот) -->
      <ActionsRow v-if="hasTopActions" :config="config" :position="['top']" level="element" :targetElementId="elementId.toString()"
        class="element-actions-top" />

      <!-- Подпись над строкой контрол+кнопка (Swing labeled editor + attachment LEFT/RIGHT). -->
      <div class="element-body">
        <label v-if="shouldHoistFieldLabel" class="element-field-label">{{ fieldLabelText }}</label>
        <!-- Основной контент элемента с действиями слева/справа -->
        <div class="element-content-wrapper">
          <ActionsRow v-if="hasLeftActions" :config="config" :position="['left']" level="element" :targetElementId="elementId.toString()"
            class="element-actions-left" />

          <div class="element-content" :class="contentClasses">
            <ElementStyleWrapper
              v-if="isSimpleLabel && simpleLabelStyleWrapper?.hasWrapper"
              :css-text="simpleLabelStyleWrapper.cssText"
              :before-html="simpleLabelStyleWrapper.beforeHtml"
              :after-html="simpleLabelStyleWrapper.afterHtml"
            >
              <div
                v-show="simpleLabelVisible"
                class="element-simple-label"
                :class="[simpleLabelAlignClass, {
                  'element-simple-label--hug-adjacent': labelHugAdjacent,
                  'element-simple-label--hug-standalone': standaloneLabelHug,
                }]"
                :style="simpleLabelInlineStyle"
              >
                <img
                  v-if="simpleLabelImageUrl"
                  :src="simpleLabelImageUrl"
                  alt=""
                  class="element-simple-label-image"
                />
                <span v-if="isSimpleLabelHtml" v-html="simpleLabelHtmlContent"></span>
                <span v-else class="element-simple-label-text">{{ simpleLabelText }}</span>
              </div>
            </ElementStyleWrapper>
            <div
              v-else-if="isSimpleLabel"
              v-show="simpleLabelVisible"
              class="element-simple-label"
              :class="[simpleLabelAlignClass, {
                'element-simple-label--hug-adjacent': labelHugAdjacent,
                'element-simple-label--hug-standalone': standaloneLabelHug,
              }]"
              :style="simpleLabelInlineStyle"
            >
              <img
                v-if="simpleLabelImageUrl"
                :src="simpleLabelImageUrl"
                alt=""
                class="element-simple-label-image"
              />
              <span v-if="isSimpleLabelHtml" v-html="simpleLabelHtmlContent"></span>
              <span v-else class="element-simple-label-text">{{ simpleLabelText }}</span>
            </div>
            <ElementRenderer
              v-else
              :config="config"
              :form-path="computedFormPath"
              :readonly="props.readonly"
              :selected="selected"
              :events="events"
              :hide-label="shouldHoistFieldLabel"
              @input="handleInput"
              @change="handleChange"
              @update:selected="updateSelected"
              @enter="handleLinkedRightActionOnEnter"
            />

            <!-- Inline actions for this element -->
            <InlineActions v-if="hasInlineActions" :element-id="elementId.toString()" :panel-id="computedFormPath.panelId"
              class="element-inline-actions" />
          </div>

          <!-- Действия справа (в той же строке, что и элемент) -->
          <ActionsRow v-if="hasRightActions" :config="config" :position="['right']" level="element" :targetElementId="elementId.toString()"
            class="element-actions-right"
            :class="{ 'element-actions-right--split': splitStandaloneIconActionRow }" />
        </div>
      </div>

      <!-- Действия снизу -->
      <ActionsRow v-if="hasBottomActions" :config="config" :position="['bottom']" level="element" :targetElementId="elementId.toString()"
        class="element-actions-bottom" />
    </MRow>
  </MCol>
</template>

<script setup lang="ts">
import { evaluateLayoutCondition } from '@/common/utils/evaluateLayoutCondition'
import {
  resolveElementExplicitVisible,
  resolveWidgetElementVisible,
} from '@/common/utils/resolveActionPageState'
import { actionIdsEqual } from '@/common/utils/resolveActionRegistryEntry'
import { sanitizePlatformDisplayHtml } from '@/common/utils/html-sanitizer'
import { resolveSimpleLabelAlignClass } from '@/common/utils/resolveSimpleLabelAlignClass'
import {
  resolveSimpleLabelSwingImageUrl,
  resolveXmlLabelFontSizePx,
} from '@/common/utils/resolveSimpleLabelPresentation'
import {
  labelRegistryKey,
  resolveLabelDisplayText,
  resolveLabelRefillText,
} from '@/common/utils/resolveLabelRefillText'
import { useActions } from '@/common/composables/useActions'
import { useDialog } from '@/common/composables/useDialog'
import { useFormStates, usePageErrors, useAlerts } from '@/common/composables'
import { usePageData } from '@/common/composables/usePageData'
import { useActionRoleAccess } from '@/common/composables/useActionRoleAccess'
import { resolveFirstElementLayoutAction } from '@/common/utils/elementLayoutActions'
import {
  normalizeFormElementType,
  shouldHoistElementFieldLabel,
  shouldSkipXmlHeightOnElementContainer,
} from '@/common/utils/formElementType'
import { resolveTilesRowScopedRegistryName } from '@/common/utils/resolveTilesRowScopedRegistryName'
import { resolveTilesInlineTargetVisible } from '@/common/utils/tilesSetInlineVisibility'
import {
  ElementContextKey,
  FormProviderKey,
  GetFormKey,
  GetPageDataKey,
  ReloadElementKey,
  SwingTilesActionVisibilityMapKey,
  SwingTilesEmbedKey,
  SwingTilesHasInlineAfterLoadKey,
  SwingTilesInlineVisibilityTargetsKey,
  SwingTilesRowScopeKey,
} from '@/components/platform/injection-keys'
import { executePageLayoutAction } from '@/services/action/executePageLayoutAction'
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/app'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'

// Components
import MCol from '@/components/ui/MCol.vue'
import MRow from '@/components/ui/MRow.vue'
import ActionsRow from './ActionsRow.vue'
import ElementRenderer from './ElementRenderer.vue'
import ElementStyleWrapper from './ElementStyleWrapper.vue'
import InlineActions from './InlineActions.vue'
import { expandActionNamesForEventsProp } from '@/components/platform/events/event-action-name-candidates'
import { shouldSplitStandaloneIconActionRow } from '@/common/utils/splitStandaloneIconActionRow'
import { resolveElementStyleWrapper } from '@/common/utils/elementStyleWrapper'

interface ElementSetupStats {
  scheduled: boolean
  count: number
  byType: Record<string, number>
  durationByType: Record<string, number>
  firstAt: number
  lastAt: number
  lastType: string
}

function getElementSetupStats(): ElementSetupStats {
  const key = '__workflowElementSetupStats'
  const root = globalThis as typeof globalThis & Record<string, ElementSetupStats | undefined>
  if (!root[key]) {
    root[key] = {
      scheduled: false,
      count: 0,
      byType: {},
      durationByType: {},
      firstAt: 0,
      lastAt: 0,
      lastType: '',
    }
  }
  return root[key]!
}

function resetElementSetupStats(stats: ElementSetupStats): void {
  stats.scheduled = false
  stats.count = 0
  stats.byType = {}
  stats.durationByType = {}
  stats.firstAt = 0
  stats.lastAt = 0
  stats.lastType = ''
}

const elementSetupStats = getElementSetupStats()

function recordElementSetup(type: string): void {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
  if (elementSetupStats.lastAt && elementSetupStats.lastType) {
    elementSetupStats.durationByType[elementSetupStats.lastType] =
      (elementSetupStats.durationByType[elementSetupStats.lastType] ?? 0) +
      (now - elementSetupStats.lastAt)
  }
  elementSetupStats.count += 1
  elementSetupStats.byType[type || 'UNKNOWN'] = (elementSetupStats.byType[type || 'UNKNOWN'] ?? 0) + 1
  if (!elementSetupStats.firstAt) {
    elementSetupStats.firstAt = now
  }
  elementSetupStats.lastAt = now
  elementSetupStats.lastType = type || 'UNKNOWN'

  if (elementSetupStats.scheduled) return
  elementSetupStats.scheduled = true
  setTimeout(() => {
    const flushAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const setupSpanMs = elementSetupStats.lastAt - elementSetupStats.firstAt
    const postSetupFlushDelayMs = flushAt - elementSetupStats.lastAt
    startWorkflowMeasure('Element.setup.batch', {
      count: elementSetupStats.count,
      byType: { ...elementSetupStats.byType },
      durationByType: Object.fromEntries(
        Object.entries(elementSetupStats.durationByType).map(([key, value]) => [
          key,
          Math.round(value * 10) / 10,
        ]),
      ),
      setupSpanMs: Math.round(setupSpanMs * 10) / 10,
      postSetupFlushDelayMs: Math.round(postSetupFlushDelayMs * 10) / 10,
      spanMs: Math.round((flushAt - elementSetupStats.firstAt) * 10) / 10,
    })()
    resetElementSetupStats(elementSetupStats)
  }, 0)
}

interface Props {
  config: any
  formPath?: any
  readonly?: boolean
  columnIndex?: number
  labelHugAdjacent?: boolean
  standaloneLabelHug?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  formPath: () => ({}),
  readonly: false,
  columnIndex: undefined,
  labelHugAdjacent: false,
  standaloneLabelHug: false,
})
const emit = defineEmits<{
  input: [data: any]
  change: [data: any]
  'update:selected': [selected: any[]]
  onAction: [action: any]
}>()

// Inject из провайдеров формы
const elementContext = inject(ElementContextKey)
const swingTilesEmbed = inject(SwingTilesEmbedKey, false)
const swingTilesRowScope = inject(SwingTilesRowScopeKey, null)
const tilesActionVisibilityMap = inject(SwingTilesActionVisibilityMapKey, null)
const tilesHasInlineAfterLoad = inject(SwingTilesHasInlineAfterLoadKey, null)
const tilesInlineVisibilityTargets = inject(SwingTilesInlineVisibilityTargetsKey, null)

const formProvider = inject(FormProviderKey, null)
const getForm = inject(GetFormKey, () => ({}))
const getPageData = inject(GetPageDataKey, () => ({}))
const reloadElement = inject(ReloadElementKey, async () => {})
const router = useRouter()
const actions = useActions()
const dialog = useDialog()
const pageErrors = usePageErrors()
const formStates = useFormStates()
const alerts = useAlerts()

const { pageData, pageFields, pageActions, pageDataSlot, pageRegistryFormId, pageWindowInstanceId, pageStates } = usePageData()
const appStore = useAppStore()
const { currentPlatform } = storeToRefs(appStore)

// Reactive state
const selected = ref<any[]>([])
const condition = ref(true)
const hydrated = ref(false)
const simpleLabelEditable = ref(true)
const simpleLabelOverride = ref<string | null>(null)

nextTick(() => {
  hydrated.value = true
})

// Computed properties
const element = computed(() => props.config.element || null)
const elementId = computed(() => +element.value?._attributes?.id || -1)
const formId = computed(() => props.formPath.formId || -1)
const panelId = computed(() => props.formPath.panelId || -1)

const computedFormPath = computed(() => ({
  ...props.formPath, // Base path from parent
  elementId: elementId.value,
  panelId: elementContext?.panelId ?? props.formPath?.panelId,
  formId: elementContext?.formId ?? props.formPath?.formId,
}))

const events = computed(() => {
  const evs = pageActions.value.filter(
    (i: any) => +i.layout?.id?._text === elementId.value && i.layout?.position?._text === 'events',
  )
  return evs.reduce((acc: any, ev: any) => {
    const rawName = ev.name?._text ?? ev.name
    for (const key of expandActionNamesForEventsProp(String(rawName ?? ''))) {
      acc[key] = ev
    }
    return acc
  }, {})
})
const computedClasses = computed(() => {
  const classes: string[] = ['element-container']

  // Классы для разных типов элементов
  const type = elementType.value
  if (type) {
    classes.push(`element-type-${type.toLowerCase()}`)
  }
  const cellColspan = parseInt(String(props.config?._attributes?.colspan ?? '1'), 10) || 1
  if (cellColspan > 1) {
    classes.push('element-container--colspan')
  }

  if (type === 'LINEDELIMITER' && element.value?.line_direction?._text === 'VERTICAL') {
    classes.push('element-type-linedelimiter--vertical')
  }

  // Выравнивание из XML (standalone hug: Swing NORTHWEST, XML right is a no-op)
  const align = element.value?.align?._text
  if (align && !props.standaloneLabelHug) {
    classes.push(`text-align-${align}`)
  }

  // Стили шрифта из XML
  const fontStyle = element.value?.FontStyle?._text
  if (fontStyle) {
    classes.push(`font-style-${fontStyle.toLowerCase()}`)
  }

  if (elementType.value === 'LISTBOX' || elementType.value === 'COMBOBOX') {
    classes.push('element-container--listbox')
  }

  if (elementType.value === 'DATE' || elementType.value === 'DATETIME') {
    classes.push('element-container--datetime')
  }

  if (type === 'LABEL') {
    if (isMultilineLabel.value) {
      classes.push('element-type-label--multiline')
    }
    if (hasRightActions.value) {
      classes.push('element-type-label--with-right-actions')
    }
    if (hasLeftActions.value) {
      classes.push('element-type-label--with-left-actions')
    }
  }

  if (type === 'TEXT' && isMultilineText.value) {
    classes.push('element-type-text--multiline')
  }
  if (type === 'TEXTAREA' || type === 'MULTILINETEXT') {
    classes.push('element-type-text--multiline')
  }

  return classes
})

/** paddingTop/Bottom/Left/Right с td */
const parseTdPaddingPx = (val: unknown): number | null => {
  if (val == null || val === '') return null
  const n = parseInt(String(val), 10)
  if (Number.isNaN(n) || n < 0) return null
  return n
}

const parseXmlElementColor = (raw: unknown): string | undefined => {
  let color = ''
  if (typeof raw === 'string' || typeof raw === 'number') {
    color = String(raw).trim()
  } else if (raw && typeof raw === 'object' && '_text' in (raw as object)) {
    color = String((raw as { _text?: unknown })._text ?? '').trim()
  } else {
    color = String(raw ?? '').trim()
  }
  if (!color || color === '[object Object]') return undefined

  if (/^0x/i.test(color)) {
    return `#${color.replace(/^0x/, '')}`
  }
  return color
}

const readXmlElementText = (node: unknown): string => {
  if (node == null) return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node).trim()
  if (typeof node === 'object' && '_text' in (node as object)) {
    return String((node as { _text?: unknown })._text ?? '').trim()
  }
  return ''
}

const isTableLikeElementType = (type: string) =>
  type === 'TABLE' ||
  type === 'EDITABLE' ||
  type === 'TREETABLE' ||
  type === 'GROUPTABLE' ||
  type === 'LISTBOXTABLE'

const computedStyle = computed(() => {
  const styles: any = {
    position: 'relative',
  }

  // Высота из XML. TABLE height=auto → min-height:auto blocks leftover shrink (WUI-526).
  // TEXT/TEXTAREA Nr: rows on the editor, not minHeight on the labeled cell.
  const height = element.value?.height?._text
  if (height) {
    const parsed = parseHeight(height)
    const skipTextareaHeight = shouldSkipXmlHeightOnElementContainer(elementType.value)
    if (
      !skipTextareaHeight &&
      !(isTableLikeElementType(elementType.value) && (height === 'auto' || parsed === 'auto'))
    ) {
      styles.minHeight = parsed
    }
  }

  // Цвет из XML (также на .element-simple-label — см. simpleLabelInlineStyle)
  const color = parseXmlElementColor(element.value?.color)
  if (color) {
    styles.color = color
  }

  // LineDelimiter: отступы как у ячейки td
  if (elementType.value === 'LINEDELIMITER') {
    const attrs = props.config?._attributes || {}
    const isVertical = element.value?.line_direction?._text === 'VERTICAL'
    if (isVertical) {
      const pl = parseTdPaddingPx(attrs.paddingLeft)
      const pr = parseTdPaddingPx(attrs.paddingRight)
      if (pl !== null) styles.marginLeft = `${pl}px`
      if (pr !== null) styles.marginRight = `${pr}px`
    } else {
      const pt = parseTdPaddingPx(attrs.paddingTop)
      const pb = parseTdPaddingPx(attrs.paddingBottom)
      styles.marginTop = `${pt !== null ? pt : 10}px`
      styles.marginBottom = `${pb !== null ? pb : 10}px`
    }
  }

  return styles
})

/** У LineDelimiter не дублируем стандартный padding MCol — отступы задаём margin на корне (как td). */
const mColPadding = computed(() => {
  if (elementType.value === 'LINEDELIMITER') return '0'
  if (elementType.value === 'LISTBOX' || elementType.value === 'COMBOBOX') {
    return '10px 4px 0 8px'
  }
  if (elementType.value === 'DATE' || elementType.value === 'DATETIME') {
    return '10px 4px 0 4px'
  }
  if (elementType.value === 'LABEL') {
    const align = String(element.value?.align?._text ?? 'left').trim().toLowerCase()
    if (align === 'right') return '10px 6px 0 10px'
  }
  return undefined
})

const contentClasses = computed(() => {
  const fieldName = element.value?.name?._text || element.value?.fieldName?._text || ''
  return {
    'element-content-filled': true,
    'element-content-empty': false,
    'element-content-fixed-width': fieldName === 'messageText' && hasRightActions.value,
  }
})

const elementType = computed(() => {
  if (!element.value) return ''
  return normalizeFormElementType(element.value) ?? ''
})

const isSimpleLabel = computed(() => elementType.value === 'LABEL')

/** Широкая/объединённая ячейка — перенос строк, без ellipsis. */
const isMultilineLabel = computed(() => {
  if (elementType.value !== 'LABEL') return false
  const cols = Number(props.config._attributes?.cols) || 0
  const colspan = parseInt(String(props.config._attributes?.colspan ?? '1'), 10) || 1
  return colspan >= 2 || cols >= 22
})

const name = computed(() => element.value?.name?._text || '')

const tilesInlineTargetVisible = computed(() => {
  if (!swingTilesRowScope) return undefined
  return resolveTilesInlineTargetVisible({
    elementName: name.value,
    rowIndex: swingTilesRowScope.rowIndex,
    hasInlineAfterLoad: Boolean(tilesHasInlineAfterLoad?.value),
    targets: tilesInlineVisibilityTargets?.value,
    rowMap: tilesActionVisibilityMap?.value,
  })
})

/** Читается из GlobalProvider — один источник для всех mount одного elementId (WUI-386). */
const simpleLabelVisible = computed(() => {
  const inline = tilesInlineTargetVisible.value
  if (inline !== undefined) return inline
  const map = formProvider?.elementShellVisibleByElementId?.value
  const shared = map?.[String(elementId.value)]
  return typeof shared === 'boolean' ? shared : true
})
const registrationName = computed(() =>
  name.value ||
  String(element.value?._attributes?.name ?? element.value?._attributes?.id ?? elementId.value ?? ''),
)

const labelRegistryName = computed(() =>
  labelRegistryKey(registrationName.value, elementId.value ?? ''),
)

const tilesRowScopedRegistrationName = computed(() =>
  resolveTilesRowScopedRegistryName(swingTilesRowScope, registrationName.value),
)

const effectiveLabelName = computed(() =>
  tilesRowScopedRegistrationName.value,
)

const effectiveLabelRegistryName = computed(() =>
  swingTilesRowScope ? tilesRowScopedRegistrationName.value : labelRegistryName.value,
)

const simpleLabelText = computed(() =>
  resolveLabelDisplayText({
    labelName: name.value,
    elementId: elementId.value,
    description: element.value?.description?._text ?? '',
    pageFields: pageFields.value as Record<string, unknown> | undefined,
    override: simpleLabelOverride.value,
  }),
)

const simpleLabelAlign = computed(() => element.value?.align?._text || 'left')

const simpleLabelImageUrl = computed(() => {
  if (!isSimpleLabel.value) return ''
  return resolveSimpleLabelSwingImageUrl(readXmlElementText(element.value?.image))
})

/**
 * Swing: XML color / FontStyle / font on Label.
 * Theme (spd.css) uses !important — set CSS vars so they win.
 */
const simpleLabelInlineStyle = computed(() => {
  if (!isSimpleLabel.value) return undefined
  const styles: Record<string, string> = {}
  const color = parseXmlElementColor(element.value?.color)
  if (color) {
    styles.color = color
    styles['--simple-label-color'] = color
  }
  const fontStyle = readXmlElementText(element.value?.FontStyle).toUpperCase()
  if (fontStyle.includes('BOLD')) {
    styles.fontWeight = '700'
    styles['--simple-label-font-weight'] = '700'
  }
  const fontSize = resolveXmlLabelFontSizePx(readXmlElementText(element.value?.font))
  if (fontSize) {
    styles.fontSize = fontSize
    styles['--simple-label-font-size'] = fontSize
  }
  return Object.keys(styles).length > 0 ? styles : undefined
})

const isSimpleLabelHtml = computed(() => {
  const value = simpleLabelText.value
  if (!value || typeof value !== 'string') return false
  const trimmed = value.trim()
  return trimmed.toLowerCase().startsWith('<html>') || trimmed.toLowerCase().startsWith('<html ')
})

const simpleLabelHtmlContent = computed(() => {
  const value = simpleLabelText.value
  if (!isSimpleLabelHtml.value || typeof value !== 'string') return ''
  return sanitizePlatformDisplayHtml(value)
})

const simpleLabelStyleWrapper = computed(() => {
  if (!isSimpleLabel.value || !element.value) return null
  return resolveElementStyleWrapper(element.value)
})

recordElementSetup(elementType.value)

// Check if element should grow vertically (like Swing's wantGrowVertically)
const shouldGrowVertically = computed(() => {
  if (!element.value) return false
  const grabVertical = element.value.grabVertical?._text
  return grabVertical === 'true' || grabVertical === '1'
})

const getSimpleLabelContext = () => ({
  pageData: pageData.value,
  pageFields: pageFields.value,
  pageActions: pageActions.value,
  element: element.value,
  formPath: computedFormPath.value,
  selected: selected.value,
  editable: simpleLabelEditable.value,
  isVisible: condition.value && simpleLabelVisible.value,
  focused: false,
})

const applySimpleLabelVisible = (value: boolean) => {
  formProvider?.setElementShellVisibleById?.(elementId.value, value)
}

const coerceLabelStateBoolean = (value: unknown): boolean | undefined => {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'boolean') return value
  const text = String(value).trim().toLowerCase()
  if (text === 'true' || text === '1') return true
  if (text === 'false' || text === '0') return false
  return Boolean(value)
}

const simpleLabelInitialStatesApplied = ref(false)

/** Однократно применить is_visible из jsResult.states (не перетирать onChange/onLoad). */
const syncSimpleLabelVisibilityFromServer = () => {
  if (simpleLabelInitialStatesApplied.value) return
  if (!isSimpleLabel.value || !formProvider || elementId.value <= 0) return

  // onLoad/reloadForm already decided via setElementShellVisibleById — do not undo from stale states.
  const shellMap = formProvider.elementShellVisibleByElementId?.value
  if (shellMap && typeof shellMap[String(elementId.value)] === 'boolean') {
    simpleLabelInitialStatesApplied.value = true
    return
  }

  const fp = formProvider as {
    setVisibleByLevel?: (level: string, id: string | number, visible: boolean) => void
  }
  if (typeof fp.setVisibleByLevel !== 'function') return

  for (const state of pageStates.value ?? []) {
    if (String(state?.element_level ?? '').toUpperCase() === 'ACTION') continue
    const stateName = String(state?.element_name ?? '').trim()
    const idMatch = actionIdsEqual(state?.element_id, elementId.value)
    const nameMatch =
      !idMatch &&
      stateName !== '' &&
      (stateName === name.value || stateName === registrationName.value)
    if (!idMatch && !nameMatch) continue
    const visible = coerceLabelStateBoolean(state.is_visible)
    if (visible === undefined) continue
    const level = String(state.element_level ?? 'element').trim().toLowerCase() || 'element'
    fp.setVisibleByLevel(level, elementId.value, visible)
    simpleLabelInitialStatesApplied.value = true
    return
  }
}

const canRegisterSimpleLabel = computed(() => {
  if (!isSimpleLabel.value || !formProvider || !effectiveLabelName.value) return false
  const { formId: fid } = computedFormPath.value
  // Page-level Labels (no real panel) still need registry for en.setVisible(name, …).
  return Boolean(fid && elementId.value > 0)
})

const registerSimpleLabel = () => {
  if (!canRegisterSimpleLabel.value) return
  formProvider!.registerElement({
    name: effectiveLabelName.value,
    registryName: effectiveLabelRegistryName.value,
    value: simpleLabelText.value,
    element: element.value,
    type: elementType.value,
    elementId: elementId.value,
    panelId: computedFormPath.value.panelId,
    formId: computedFormPath.value.formId,
    ctx: getSimpleLabelContext(),
    doNotSerialize: true,
    hooks: {
      getContext: getSimpleLabelContext,
      getSelected: () => selected.value,
      getSuffixes: () => [''],
      setVisible: applySimpleLabelVisible,
      setEditable: (value: boolean) => {
        simpleLabelEditable.value = value
      },
      refill: (data: any) => {
        const text = resolveLabelRefillText(data, registrationName.value)
        if (text) {
          simpleLabelOverride.value = text
        }
      },
      empty: () => {
        simpleLabelOverride.value = ''
      },
    },
  })
}

watch(
  () => [
    canRegisterSimpleLabel.value,
    registrationName.value,
    computedFormPath.value.formId,
    computedFormPath.value.panelId,
    elementId.value,
  ],
  () => {
    registerSimpleLabel()
  },
  { immediate: true },
)

watch(
  () => pageStates.value,
  () => {
    if (isSimpleLabel.value && !simpleLabelInitialStatesApplied.value) {
      syncSimpleLabelVisibilityFromServer()
    }
  },
  { deep: true },
)

onMounted(() => {
  if (isSimpleLabel.value) {
    syncSimpleLabelVisibilityFromServer()
  }
  const type = elementType.value
  if (type === 'TABLE' || type === 'EDITABLE' || type === 'TREETABLE' || type === 'GROUPTABLE' || type === 'MULTISELECT') {
    startWorkflowMeasure('Element.onMounted', {
      type,
      elementId: elementId.value,
      name: name.value,
      formId: computedFormPath.value.formId,
      panelId: computedFormPath.value.panelId,
    })()
  }
})

onUnmounted(() => {
  if (!isSimpleLabel.value || !formProvider || !effectiveLabelName.value) return
  formProvider.unRegisterElement({
    ...computedFormPath.value,
    fieldName: effectiveLabelName.value,
    registryName: effectiveLabelRegistryName.value,
  })
})

const registeredElement = computed(() => {
  const getElements = (formProvider as any)?.getElements
  if (typeof getElements !== 'function' || !name.value) {
    return null
  }

  const elements = getElements()
  if (!elements || typeof elements !== 'object') {
    return null
  }

  return (
    (elements as Record<string, any>)[effectiveLabelRegistryName.value] ??
    (elements as Record<string, any>)[labelRegistryName.value] ??
    (elements as Record<string, any>)[name.value] ??
    null
  )
})

const isElementVisible = computed(() => {
  const inline = tilesInlineTargetVisible.value
  if (inline !== undefined) return condition.value && inline
  if (isSimpleLabel.value) {
    return condition.value && simpleLabelVisible.value
  }
  const shellMap = formProvider?.elementShellVisibleByElementId?.value
  const shellVisible =
    elementId.value > 0 ? shellMap?.[String(elementId.value)] : undefined
  return resolveWidgetElementVisible({
    shellVisible,
    statesVisible: resolveElementExplicitVisible(pageStates.value, elementId.value, name.value),
    registryVisible: registeredElement.value?.getContext?.()?.isVisible,
    defaultVisible: true,
  })
})

const simpleLabelVisibilityStyle = computed(() => {
  if (!isSimpleLabel.value) return {}
  return isElementVisible.value ? {} : { display: 'none' }
})

const widthMode = computed((): 'content' | 'grow' | undefined => {
  const mode = props.config._attributes?.widthMode
  return mode === 'content' || mode === 'grow' ? mode : undefined
})
const cols = computed(() => {
  if (widthMode.value === 'content') return 0
  const raw = props.config._attributes?.cols
  if (raw == null || raw === '') return 100
  const n = Number(raw)
  return Number.isFinite(n) ? n : 100
})
/** Высокий TEXT/TEXTAREA: right-action тянется на высоту поля (Swing attachment RIGHT). */
const isMultilineText = computed(() => {
  const type = elementType.value
  return type === 'TEXTAREA' || type === 'MULTILINETEXT'
})
const elementRowAlign = computed(() => {
  if (isMultilineText.value) return 'stretch' as const
  if (isTableLikeElementType(elementType.value)) return 'stretch' as const
  return 'center' as const
})
const conditionAttr = computed(() =>
  props.config?._attributes?.condition ?? props.config?.element?._attributes?.condition ?? '',
)
const hasCondition = computed(() => String(conditionAttr.value ?? '').trim() !== '')

const { hasActionRoleAccess } = useActionRoleAccess()

const hasRequiredRoleForAction = (action: any): boolean => hasActionRoleAccess(action.roles)

const getDropDownName = (action: any): string => {
  const dropDown = action.dropDownActionName
  if (Array.isArray(dropDown)) {
    return dropDown[0]?._text?.trim() || ''
  }
  return dropDown?._text?.trim() || ''
}

const isDropDownMain = (action: any): boolean => {
  const dropDown = action.dropDownMain
  const value = Array.isArray(dropDown) ? dropDown[0]?._text : dropDown?._text
  return value === 'true' || value === true
}

const elementActionPositions = computed(() => {
  const positions = new Set<string>()
  const elementIdStr = elementId.value.toString()

  for (const action of pageActions.value) {
    if (action.layout?.level?._text !== 'element') continue
    if (action.layout?.id?._text !== elementIdStr) continue
    if (!hasRequiredRoleForAction(action)) continue

    const dropDownName = getDropDownName(action)
    if (dropDownName && !isDropDownMain(action)) continue

    const position = action.layout?.position?._text
    if (position) {
      positions.add(position)
    }
  }

  return positions
})

const hasTopActions = computed(() => elementActionPositions.value.has('top'))
const hasLeftActions = computed(() => elementActionPositions.value.has('left'))
const hasRightActions = computed(() => elementActionPositions.value.has('right'))
const splitStandaloneIconActionRow = computed(() =>
  shouldSplitStandaloneIconActionRow({
    elementType: elementType.value,
    inTilesRow: Boolean(swingTilesRowScope),
  }),
)
const fieldLabelText = computed(() => String(element.value?.description?._text ?? '').trim())
const shouldHoistFieldLabel = computed(() =>
  shouldHoistElementFieldLabel({
    isLabelType: isSimpleLabel.value,
    hasSideActions: hasLeftActions.value || hasRightActions.value,
    labelText: fieldLabelText.value,
  }),
)

/** Listbox/label: clip cell; with side-actions always visible so buttons are not clipped. */
const mColOverflow = computed(() => {
  if (hasRightActions.value || hasLeftActions.value) {
    return 'visible' as const
  }
  if (elementType.value === 'LISTBOX' || elementType.value === 'COMBOBOX') {
    return 'hidden' as const
  }
  if (elementType.value === 'LABEL') {
    return 'hidden' as const
  }
  return 'visible' as const
})

const simpleLabelColspan = computed(
  () => parseInt(String(props.config._attributes?.colspan ?? '1'), 10) || 1,
)
const simpleLabelAlignClass = computed(() =>
  resolveSimpleLabelAlignClass({
    align: simpleLabelAlign.value,
    cols: Number(cols.value) || 100,
    colspan: simpleLabelColspan.value,
    hasRightActions: hasRightActions.value,
    hugStandalone: props.standaloneLabelHug,
  }),
)
const hasBottomActions = computed(() => elementActionPositions.value.has('bottom'))
const hasStackedElementActions = computed(() => hasTopActions.value || hasBottomActions.value)

/** Enter в TEXT/PASSWORD: первый right-action (Swing executeLinkedAction на VisualEntity). */
const handleLinkedRightActionOnEnter = async () => {
  if (!hasRightActions.value) return
  if (elementType.value !== 'TEXT' && elementType.value !== 'PASSWORD') return

  const linkedAction = resolveFirstElementLayoutAction(pageActions.value, {
    targetElementId: elementId.value.toString(),
    level: 'element',
    positions: ['right'],
  })
  if (!linkedAction) return

  const formIdStr = String(computedFormPath.value.formId ?? elementContext?.formId ?? '')
  if (!formIdStr) return

  try {
    await executePageLayoutAction(
      linkedAction,
      {
        formId: formIdStr,
        panelId: computedFormPath.value.panelId != null ? String(computedFormPath.value.panelId) : undefined,
        elementId: elementId.value.toString(),
        platform: currentPlatform.value || undefined,
      },
      {
        appStore,
        router,
        actions,
        dialog,
        reloadElement,
        refillElement: formProvider?.refillElement,
        pageErrors,
        formStates,
        alerts,
        getForm,
        getPageData,
        getTableSelection: formProvider?.getTableSelection,
        runCode: formProvider?.runCode,
        formProvider,
        pageData: pageData.value,
        pageDataSlot: pageDataSlot.value,
        pageRegistryFormId: pageRegistryFormId.value,
        pageWindowInstanceId: pageWindowInstanceId.value,
      },
    )
  } catch (error) {
    console.error('Error executing linked right action on Enter:', error)
  }
}

// Check if this element has inline actions
const hasInlineActions = computed(() => {
  if (swingTilesEmbed) {
    return false
  }

  // MediaWall handles inline actions per-widget, not per-element
  if (elementType.value === 'MEDIAWALL') {
    return false
  }

  // MULTISELECT handles inline actions per-chip via context menu, not per-element
  if (elementType.value === 'MULTISELECT') {
    return false
  }

  // Table-like elements render row-level actions themselves
  const tableLikeTypes = ['TABLE', 'EDITABLE', 'TREETABLE', 'GROUPTABLE', 'TILES']
  if (tableLikeTypes.includes(elementType.value)) {
    return false
  }

  const elementIdStr = elementId.value.toString()

  const inlineActions = pageActions.value.filter((action: any) => {
    const actionLevel = action.layout?.level?._text
    const actionId = action.layout?.id?._text
    const actionPosition = action.layout?.position?._text

    const isInline =
      actionLevel === 'element' && actionId === elementIdStr && actionPosition === 'inline'

    const hasRole = hasRequiredRoleForAction(action)

    return isInline && hasRole
  })

  return inlineActions.length > 0
})

// Methods
const parseHeight = (height: string) => {
  if (!height) return 'auto'

  // Обработка высоты в строках (например, "3r")
  if (typeof height === 'string' && height.endsWith('r')) {
    const lines = parseInt(height)
    return `${lines * 24}px`
  }

  // Обработка пикселей
  if (typeof height === 'string' && height.endsWith('px')) {
    return height
  }

  // Числовое значение
  if (!isNaN(Number(height))) {
    return `${height}px`
  }

  return 'auto'
}

const handleInput = (data: any) => {
  emit('input', data)
}

const handleChange = (data: any) => {
  emit('change', data)
}

const updateSelected = (selectedData: any[]) => {
  selected.value = selectedData
  emit('update:selected', selectedData)
}

let lastConditionEvaluation = 0

const setCondition = async () => {
  const rawCondition = String(conditionAttr.value ?? '').trim()
  if (!rawCondition) {
    condition.value = true
    return
  }

  const evaluationId = ++lastConditionEvaluation
  const visible = await evaluateLayoutCondition({
    condition: rawCondition,
    formProvider: formProvider as Parameters<typeof evaluateLayoutCondition>[0]['formProvider'],
    pageData: pageData.value,
    formId: formId.value,
    panelId: panelId.value,
    elementId: elementId.value,
    measureLabel: 'Element.condition.evaluate',
  })
  if (evaluationId !== lastConditionEvaluation) {
    return
  }
  condition.value = visible
}

watch(
  [
    conditionAttr,
    () => formId.value,
    () => panelId.value,
    () => elementId.value,
  ],
  () => {
    void setCondition()
  },
  {
    immediate: true,
    flush: 'post',
  },
)

watch(
  () => (hasCondition.value ? appStore.pagesDataRevision : -1),
  () => {
    if (!hasCondition.value) return
    void setCondition()
  },
  { flush: 'post' },
)
</script>

<style scoped lang="scss">
.element-container {
  min-height: 40px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  &-row {
    gap: 8px !important;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  &-row--stacked-actions {
    flex-direction: column !important;
    align-items: stretch !important;
    width: 100%;
  }
}

.element-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  width: 100%;
}

.element-field-label {
  display: block;
  padding-bottom: 8px;
  font-weight: bold;
  color: #374151;
  width: 100%;
  box-sizing: border-box;
}

.element-content-wrapper {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  padding-top: 0;
  box-sizing: border-box;
}

/*
 * Right-actions (Swing linked button): one control — field shrinks, button keeps title width.
 * overflow:hidden on wrapper prevents escape under panel clip; do not use visible here.
 */
.element-content-wrapper:has(.element-actions-right) {
  overflow: hidden;
}

.element-content-wrapper:has(.element-actions-right) .element-content {
  flex: 1 1 0%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

/* Для messageText с rightActions — не растягиваем на всю ширину */
.element-content-wrapper:has(.element-actions-right) .element-content.element-content-fixed-width {
  flex: 0 1 auto !important;
}

.element-content-wrapper:has(.element-actions-right) :deep(.p-inputtext),
.element-content-wrapper:has(.element-actions-right) :deep(.p-autocomplete-input-multiple) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: none;
}

.element-content-wrapper:has(.element-actions-left) .element-content {
  flex: 1 1 0%;
  min-width: 0;
  max-width: 100%;
}

.element-content {
  flex: 1;
  min-height: 0;
  min-width: 0;

  &-filled {
    // Стили для заполненного контента
  }

  &-empty {
    // Стили для пустого контента
  }

  &-fixed-width {
    // Для messageText с rightActions — не растягиваем на всю ширину
    flex: 0 1 auto !important;
  }
}


.element-simple-label {
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  min-height: 24px;
  padding: 4px 0;
  color: inherit;
  line-height: 1.2;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.element-simple-label--align-left {
  justify-content: flex-start;
  text-align: left;
  align-self: stretch;
}

.element-simple-label--align-center {
  justify-content: center;
  text-align: center;
}

.element-simple-label--align-right {
  justify-content: flex-end;
  width: 100%;
  min-width: 0;
  text-align: right;
  padding-left: 6px;
}

.element-simple-label--hug-adjacent {
  justify-content: flex-end;
  text-align: right;
  width: 100%;
}

.element-simple-label--hug-standalone {
  width: fit-content;
  max-width: 100%;
  align-self: flex-start;
}

.element-simple-label--hug-standalone .element-simple-label-text {
  width: auto;
}

.element-simple-label-image {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  object-fit: contain;
  margin-right: 4px;
}

.element-simple-label-text {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
}

.element-container--listbox :deep(.element-content-wrapper),
.element-container--listbox :deep(.element-content) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.element-container--listbox :deep(.m-selector-wrapper) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.element-container--datetime {
  display: flex;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: visible;
  box-sizing: border-box;
}

.element-container--listbox {
  display: flex;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  isolation: isolate;
  overflow: hidden;
}

.element-container--listbox :deep(.m-selector),
.element-container--listbox :deep(.p-select) {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  overflow: hidden;
}

.element-container--listbox :deep(.p-select-label) {
  max-width: 100%;
  min-width: 0;
  min-height: 0 !important;
  height: auto !important;
  padding: 0.35rem 0.5rem !important;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.element-container--listbox :deep(.p-select-trigger) {
  min-height: 0 !important;
  height: auto !important;
}

.element-container:has(.element-actions-bottom) {
  display: flex;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.element-container.element-type-label {
  position: relative;
  min-width: 0;
  overflow: hidden;
}

.element-container.element-type-label.element-type-label--with-left-actions,
.element-container.element-type-label.element-type-label--with-right-actions {
  overflow: visible;
}

.element-type-label {
  .element-content-wrapper,
  .element-content {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .element-simple-label {
    overflow: hidden;
  }
}

.element-type-label.element-type-label--with-left-actions {
  .element-content-wrapper {
    overflow: visible;
  }
}

.element-actions-top,
.element-actions-bottom,
.element-actions-left,
.element-actions-right {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.element-actions-top {
  justify-content: flex-start;
  margin-bottom: 4px;
  width: 100%;
  flex-wrap: nowrap;
}

.element-actions-bottom {
  justify-content: flex-start;
  width: 100%;
  flex-wrap: nowrap;

  :deep(.element-actions) {
    flex-wrap: nowrap;
    justify-content: flex-start;
  }
}

.element-actions-left {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
}

.element-actions-right {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;

  :deep(.panel-actions) {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    flex-wrap: nowrap;
  }

  :deep(.action) {
    align-items: center;
    margin: 0;
  }

  :deep(.p-button) {
    margin: 0 !important;
    box-sizing: border-box;
    line-height: 1;
  }
}

// Стили выравнивания
.text-align-right {
  justify-content: flex-end;
  text-align: right;
}

.text-align-center {
  justify-content: center;
  text-align: center;
}

.text-align-left {
  justify-content: flex-start;
  text-align: left;
}

// Стили шрифта
.font-style-bold {
  font-weight: 600;
}

.font-style-italic {
  font-style: italic;
}

// Стили для разных типов элементов
.element-type-text,
.element-type-password {
  // Стили для текстовых полей
}

.element-type-textarea {
  // Стили для текстовых областей
}

.element-type-listbox,
.element-type-combobox {
  // Стили для выпадающих списков
}

.element-type-boolean,
.element-type-checkbox {
  // Стили для чекбоксов
  align-items: start;
}

.element-type-table,
.element-type-editable,
.element-type-treetable,
.element-type-grouptable {
  min-height: 120px;
}

/* Tiles / tables: не inline-flex shrink-to-fit — иначе дети с width:100% схлопываются в 0×0. */
.element-container.element-type-table,
.element-container.element-type-editable,
.element-container.element-type-treetable,
.element-container.element-type-grouptable,
.element-container.element-type-tiles {
  display: flex;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  align-self: stretch;
  box-sizing: border-box;
}

.element-container.element-type-tiles {
  min-height: 40px;
}

.element-container.element-type-table .element-content-wrapper,
.element-container.element-type-editable .element-content-wrapper,
.element-container.element-type-treetable .element-content-wrapper,
.element-container.element-type-grouptable .element-content-wrapper,
.element-container.element-type-tiles .element-content-wrapper,
.element-container.element-type-table .element-content,
.element-container.element-type-editable .element-content,
.element-container.element-type-treetable .element-content,
.element-container.element-type-grouptable .element-content,
.element-container.element-type-tiles .element-content {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.element-container.element-type-tiles .element-content-wrapper {
  align-items: stretch;
}

.element-type-label {
  // Стили для меток
  min-height: 24px;
}

/* Подпись справа + кнопки справа: зазор, перенос внутри ячейки (без гориз. скролла) */
.element-container.element-type-label.text-align-right.element-type-label--with-right-actions:not(
    .element-type-label--multiline
  ) {
  .element-content-wrapper {
    flex-wrap: nowrap;
    gap: 10px;
  }

  .element-content {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
  }
}

.element-type-linedelimiter {
  min-height: 0;
  height: 0;
  width: 100%;
  background-color: transparent;
  border-top: 1px solid #d9dee7;

  &.element-type-linedelimiter--vertical {
    width: 0;
    height: auto;
    min-height: auto;
    border-top: 0;
    border-left: 1px solid #d9dee7;
  }
}

:deep(.p-inputtext:not(.p-autocomplete-input)),
:deep(.p-textarea),
:deep(.p-autocomplete-input-multiple),
:deep(.p-select) {
  min-height: 40px;
}

// Elements with grabVertical should stretch to full height
.element-container:has([is-mediawall='true']) {
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;

  .element-body,
  .element-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
}

.element-container.element-type-text:not(.element-type-text--multiline),
.element-container.element-type-password,
.element-container.element-type-multiselect,
.element-container.element-type-directory,
.element-container.element-type-datetime,
.element-container.element-type-date,
.element-container.element-type-daterange {
  .element-container-row {
    align-items: center;
  }

  .element-content-wrapper {
    gap: 0.5rem;
    align-items: center;
    min-height: 40px;
    overflow: visible;
    height: auto;
  }

  .element-content {
    display: flex;
    align-items: center;
    min-width: 0;
    overflow: visible;
    height: auto;
  }

  .element-actions-right {
    display: inline-flex;
    align-items: center;
    align-self: center;
    gap: 0.5rem;
    height: auto;
    min-height: 0;
    flex-shrink: 0;

    :deep(.panel-actions) {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      margin: 0 !important;
      padding: 0 !important;
      flex-wrap: nowrap !important;
    }

    :deep(.action),
    :deep(.panel-action-item) {
      display: inline-flex;
      align-items: center;
      margin: 0 !important;
    }

    :deep(.p-button) {
      width: 2.5rem !important;
      height: 2.5rem !important;
      min-width: 2.5rem !important;
      min-height: 2.5rem !important;
      margin: 0 !important;
      padding: 0 !important;
      border-radius: 6px !important;
      box-sizing: border-box;
      line-height: 1;
    }
  }
}

/* Multiline TEXT: stretch to the cell. height:100% / no right border only with attachment RIGHT. */
.element-container.element-type-text--multiline,
.element-container.element-type-textarea,
.element-container.element-type-text:has(textarea),
.element-container.element-type-text:has(.p-textarea) {
  .element-container-row,
  .element-body,
  .element-content-wrapper,
  .element-content {
    align-items: stretch !important;
  }

  .element-body {
    align-self: stretch;
  }

  .element-content {
    width: 100%;
    align-self: stretch;
  }

  .element-content-wrapper {
    gap: 0;
    min-height: 0;
    min-width: 0;
    flex: 1;
  }

  :deep(.element-renderer),
  :deep(.m-textarea-wrapper),
  :deep(.p-floatlabel) {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  :deep(textarea),
  :deep(.p-textarea),
  :deep(.m-textarea) {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    resize: none;
  }

  .element-actions-right,
  .element-actions-left {
    align-self: stretch !important;
    align-items: stretch !important;
    height: auto !important;
    min-height: 0 !important;

    :deep(.element-actions),
    :deep(.action),
    :deep(.panel-action-item) {
      display: flex;
      align-items: stretch;
      align-self: stretch;
      height: 100% !important;
      min-height: 0 !important;
    }

    :deep(.p-button) {
      height: 100% !important;
      min-height: 0 !important;
      align-self: stretch !important;
    }
  }
}

.element-container.element-type-text--multiline:has(.element-actions-right),
.element-container.element-type-textarea:has(.element-actions-right),
.element-container.element-type-text:has(.element-actions-right):has(textarea),
.element-container.element-type-text:has(.element-actions-right):has(.p-textarea) {
  .element-actions-right :deep(.p-button) {
    margin: 0 !important;
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-top-right-radius: 8px !important;
    border-bottom-right-radius: 8px !important;
  }

  :deep(textarea),
  :deep(.p-textarea),
  :deep(.m-textarea) {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-right: none !important;
    height: 100%;
  }

  :deep(.m-textarea-wrapper),
  :deep(.p-floatlabel) {
    display: flex;
    height: 100%;
    margin: 0;
    padding: 0;
  }

  :deep(.element-renderer) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 0;
    height: 100%;
  }
}

// Адаптивность
@media (max-width: 768px) {
  .element-container {
    min-height: 44px;
  }
}

.element-inline-actions {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 10;
}
</style>
